import {useTranslation} from 'react-i18next';
import {DateQuestion} from '../../../forms/components/AddEditForm/types';
import {DateInput} from '../../../operations/components/Modals/CustomTiming/components/DateInput';
import {FormQuestion} from '../../../../services/operations/forms';
import {SVG} from '../../../../constants/assetLists';

type SingleDateAnswer = string;
type RangeDateAnswer = {from: string; to: string};
type DateAnswer = SingleDateAnswer | RangeDateAnswer;

type Props = {
  question: FormQuestion;
  value: DateAnswer | null;
  onChange: (value: DateAnswer | null) => void;
};

/** Parse a YYYY/MM/DD string into a local Date (avoids UTC off-by-one). */
const parseDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split('/').map(Number);
  return new Date(year, month - 1, day);
};

/** Format a Date back to a YYYY/MM/DD string. */
const formatDate = (date: Date | null): string => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

function PublicDate({question, value, onChange}: Props) {
  const {t} = useTranslation();

  const isSingle = !question?.dateConfig?.range;

  const handleSingleChange = (date: Date | null) => {
    onChange(formatDate(date) || null);
  };

  const handleRangeFromChange = (date: Date | null) => {
    const current =
      value && typeof value === 'object' ? value : {from: '', to: ''};
    onChange({...current, from: formatDate(date)});
  };

  const handleRangeToChange = (date: Date | null) => {
    const current =
      value && typeof value === 'object' ? value : {from: '', to: ''};
    onChange({...current, to: formatDate(date)});
  };

  const singleValue = typeof value === 'string' ? value : '';
  const rangeValue =
    value && typeof value === 'object'
      ? (value as RangeDateAnswer)
      : {from: '', to: ''};

  return (
    <div className="flex flex-col gap-3 mt-3">
      <div className="flex flex-wrap md:flex-nowrap items-center gap-3">
        {isSingle ? (
          /* Single date input */
          <>
            <div className="flex-1">
              <DateInput
                selected={parseDate(singleValue)}
                onChange={handleSingleChange}
                className="border-none"
                dateFormat="EEEE, d 'ב'MMMM"
                placeholderText="yyyy, mmm, dd"
                icon={
                  <img
                    src={SVG.calendarBlackIcon}
                    className="w-[18px] h-5 object-contain"
                  />
                }
              />
            </div>

            <div className="flex-1 hidden md:block" />
          </>
        ) : (
          /* Date range inputs */
          <>
            <div className="flex-1">
              <DateInput
                selected={parseDate(rangeValue.from)}
                onChange={handleRangeFromChange}
                className="border-none"
                dateFormat="EEEE, d 'ב'MMMM"
                placeholderText="yyyy, mmm, dd"
                icon={
                  <img
                    src={SVG.calendarBlackIcon}
                    className="w-[18px] h-5 object-contain"
                  />
                }
              />
            </div>

            <div className="flex-1">
              <DateInput
                selected={parseDate(rangeValue.to)}
                onChange={handleRangeToChange}
                className="border-none"
                dateFormat="EEEE, d 'ב'MMMM"
                placeholderText="yyyy, mmm, dd"
                icon={
                  <img
                    src={SVG.calendarBlackIcon}
                    className="w-[18px] h-5 object-contain"
                  />
                }
              />
            </div>
          </>
        )}

        <div className="flex-1 hidden md:block" />
      </div>
    </div>
  );
}

export default PublicDate;
